/**
 * CalendarManager.ts
 *
 * 캘린더 자체의 CRUD 작업을 담당하는 모듈
 *
 * 주요 기능:
 * - 캘린더 생성, 수정, 삭제
 * - 캘린더 정보 조회 및 설정
 * - 캘린더 공유 및 초대 관리
 * - 캘린더 색상 및 이름 변경
 * - 뷰 타입 설정 관리
 *
 * 이 모듈은 캘린더 자체의 생명주기를 관리하며,
 * 사용자가 캘린더를 생성하고 설정을 변경할 수 있도록 합니다.
 */

import settings from 'popups/Settings'

import { classInfo, folderEmitter, instanceEmitter, getUserInfo } from './CalendarState'
import { getClient } from './CalendarClient'
import { fetchCalendars } from './CalendarSync'
import { toDavPathList, isSameDavUrl } from 'libs/dav/davPath'
// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail, getPersonaEmailFromDisplayEmail } from 'utils/personaEmail'
import * as eventInstanceManager from 'pages/calendar/modules/EventInstanceManager'
import mailboxApiHelper from 'services/mailbox-api'
import { RestClientError } from 'services/restClient'

/**
 * Calendar view types
 */
export type CalendarViewType = 'month' | 'week' | 'day' | 'agenda'

/**
 * DAV response structure
 */
interface DavResponse {
  ok?: boolean
  href?: string
  [key: string]: unknown
}

/**
 * Create a new calendar
 *
 * @param name - Calendar display name
 * @param color - Calendar color (hex format)
 * @param completeCallback - Callback with success status
 */
export const createCalendar = (
  name: string,
  color: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  let xml = '<?xml version="1.0" encoding="utf-8" ?>'
  xml +=
    '<D:mkcol xmlns:D="DAV:" xmlns:CAL="urn:ietf:params:xml:ns:caldav" xmlns:CARD="urn:ietf:params:xml:ns:carddav" xmlns:n0="http://apple.com/ns/ical/">'
  xml += '<D:set>'
  xml += '<D:prop>'
  xml += '<D:resourcetype>'
  xml += '<D:collection />'
  xml += '<CAL:calendar />'
  xml += '</D:resourcetype>'
  xml += '<D:displayname>' + name + '</D:displayname>'
  xml += '<n0:calendar-color>' + color + '</n0:calendar-color>'
  xml += '<CAL:supported-calendar-component-set>'
  xml += '<CAL:comp name="VEVENT" />'
  xml += '</CAL:supported-calendar-component-set>'
  xml += '</D:prop>'
  xml += '</D:set>'
  xml += '</D:mkcol>'

  const newId = crypto.randomUUID()
  const newUrl = client.account.homeUrl + newId + '/'

  client
    .davRequest({
      url: newUrl,
      convertIncoming: false,
      init: {
        method: 'MKCOL',
        body: xml,
        headers: {
          'Content-Type': 'application/xml; charset="utf-8"',
          Depth: '0',
        },
      },
    })
    .then((response) => {
      console.log('create calendar success %o', response)

      const calendarSettings = settings.getValue('calendar')
      const newCalendar = {
        ...calendarSettings,
        calendarSyncTargets: toDavPathList([...calendarSettings.calendarSyncTargets, newUrl]),
      }
      settings
        .setValue('calendar', newCalendar)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { calendar: newCalendar },
          })
        })
        .catch((err) => console.error('Failed to sync calendar settings:', err))
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('create calendar failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Update calendar properties (name, color)
 *
 * @param calendarUrl - Calendar URL
 * @param name - New calendar name
 * @param color - New calendar color
 * @param completeCallback - Callback with success status
 */
export const updateCalendar = (
  calendarUrl: string,
  name: string,
  color: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  const oldCalendar = classInfo.calendars?.find((c) => c.url === calendarUrl)
  if (oldCalendar) {
    const newCalendar = { ...oldCalendar, displayName: name, calendarColor: color }
    classInfo.calendars = classInfo.calendars!.map((c) => {
      if (c.url === calendarUrl) {
        return newCalendar
      }
      return c
    })
    if (oldCalendar.color !== color) {
      classInfo.instances.forEach((instance) => {
        if (instance.serverId.startsWith(calendarUrl)) {
          instance.changeFolderColor(color)
        }
      })
    }
    folderEmitter.emit('folder')
  }

  let xml = '<?xml version="1.0" encoding="utf-8" ?>'
  xml += '<D:propertyupdate xmlns:D="DAV:" xmlns:A="http://apple.com/ns/ical/">'
  xml += '<D:set>'
  xml += '<D:prop>'
  xml += '<D:displayname>' + name + '</D:displayname>'
  xml += '<A:calendar-color>' + color + '</A:calendar-color>'
  xml += '</D:prop>'
  xml += '</D:set></D:propertyupdate>'
  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'PROPPATCH',
        body: xml,
      },
    })
    .then((response) => {
      console.log('update calendar success %o', response)
      fetchCalendars()
      completeCallback(true)

      const oldColor = oldCalendar?.calendarColor._cdata
      if (oldColor !== color) {
        classInfo.instances.forEach((i) => {
          if (
            i.serverId.startsWith(calendarUrl) &&
            i.color === oldColor &&
            !(i.event as any)?.color
          ) {
            ;(i as any).color = color
          }
        })
        instanceEmitter.emit('instance')
      }
    })
    .catch((error) => {
      console.log('update calendar failed : %o', error)
      if (oldCalendar) {
        classInfo.calendars = classInfo.calendars!.map((c) => {
          if (c.url === calendarUrl) {
            return oldCalendar
          }
          return c
        })
        folderEmitter.emit('folder')
      }
      completeCallback(false)
    })
}

/**
 * Delete a calendar
 *
 * @param calendarUrl - Calendar URL
 * @param completeCallback - Callback with success status
 */
export const deleteCalendar = (
  calendarUrl: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  let oldCalendar: any = null
  let oldCalendarIndex = -1
  classInfo.calendars = classInfo.calendars!.filter((c, index) => {
    if (c.url === calendarUrl) {
      oldCalendar = c
      oldCalendarIndex = index
      return false
    }
    return true
  })
  folderEmitter.emit('folder')

  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'DELETE',
        body: null,
      },
    })
    .then((response) => {
      console.log('delete calendar success %o', response)
      const calendarSettings = settings.getValue('calendar')
      const remainSyncTargets = calendarSettings.calendarSyncTargets.filter(
        (t: string) => !isSameDavUrl(t, calendarUrl),
      )
      const newCalendar = {
        ...calendarSettings,
        calendarSyncTargets: remainSyncTargets,
      }
      settings
        .setValue('calendar', newCalendar)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { calendar: newCalendar },
          })
        })
        .catch((err) => console.error('Failed to sync calendar settings:', err))
      eventInstanceManager.removeInstanceByCalendar(calendarUrl)
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('delete calendar failed : %o', error)
      if (oldCalendarIndex !== -1 && oldCalendar) {
        classInfo.calendars!.splice(oldCalendarIndex, 0, oldCalendar)
        folderEmitter.emit('folder')
      }
      completeCallback(false)
    })
}

/**
 * Get calendar information including sharing details
 *
 * @param folderUrl - Calendar URL
 * @param completeCallback - Callback with calendar info or null
 */
export const getCalendarInfo = (
  folderUrl: string,
  completeCallback: (result: DavResponse | null) => void = (_result) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(null)
    return
  }

  let xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<D:propfind xmlns:D="DAV:" xmlns:CAL="urn:ietf:params:xml:ns:caldav" xmlns:n0="http://apple.com/ns/ical/" xmlns:S="http://calendarserver.org/ns/">' +
    '<D:prop>' +
    '<S:invite>' +
    '<S:organizer/><S:invitee/><S:access/><S:invite-noresponse/>' +
    '</S:invite>' +
    '<D:resourcetype/><D:displayname/><n0:calendar-color/><CAL:calendar-description/>' +
    '<D:calendar-timezone/><CAL:supported-calendar-component-set/><S:source/><D:owner/>' +
    '</D:prop>' +
    '</D:propfind>'

  client
    .davRequest({
      url: folderUrl,
      convertIncoming: false,
      init: {
        method: 'PROPFIND',
        body: xml,
        headers: {
          Depth: '0',
        },
      },
    })
    .then((response: DavResponse[]) => {
      console.log('get calendar info success %o', response)
      completeCallback(response[0])
    })
    .catch((error) => {
      console.log('get calendar info failed : %o', error)
      completeCallback(null)
    })
}

/**
 * 페르소나 세션에서 CalDAV sharee href 로 보낼 이메일을 결정한다.
 *
 * 페르소나 세션이 아니거나, 대상이 내 내부 이메일 자체이거나,
 * 이미 페르소나 도메인으로 인코딩된 주소면 원본을 그대로 반환한다.
 */
const toShareeEmail = (toEmail: string): string => {
  const userInfo = getUserInfo()
  const realEmail = userInfo.personaEmail
  if (!realEmail || realEmail === toEmail || isPersonaEmail(toEmail)) {
    return toEmail
  }
  return getPersonaEmailFromDisplayEmail(toEmail, userInfo.personaDomain as string)
}

/**
 * 초대 사전 검증 결과
 */
export interface InviteeCheckResult {
  /** 입력받은 표시용 주소 그대로 */
  email: string
  invitable: boolean
}

/**
 * 초대 대상들의 초대 가능 여부를 mailbox-api 로 사전 검증한다.
 *
 * 표시용 주소를 toShareeEmail 로 변환한 뒤 local part 만 전송한다.
 * (서버는 JWT 토큰의 도메인을 사용하므로, 가상 도메인 세션에서는 인코딩된 local part 를 보내야 한다.)
 *
 * 404(해당 도메인에 계정 없음)는 초대 불가로 판정하지만, 그 외 오류는 fail-open 으로 초대 가능 처리한다.
 * 검증 API 장애 시 초대를 전면 차단하면 검증 도입 이전보다 후퇴하므로, 판단 불가면 기존처럼 초대를 시도한다.
 *
 * @param emails - 조직도가 준 표시용 이메일 목록
 * @returns 입력 순서와 동일한 검증 결과 목록
 */
export const checkInvitees = async (emails: string[]): Promise<InviteeCheckResult[]> => {
  if (emails.length === 0) {
    return []
  }

  return await Promise.all(
    emails.map(async (email): Promise<InviteeCheckResult> => {
      const localPart = toShareeEmail(email).split('@')[0]
      try {
        const res = await mailboxApiHelper.checkInvitee(localPart)
        return { email, invitable: res.data.invitable }
      } catch (error) {
        if (error instanceof RestClientError && error.status === 404) {
          return { email, invitable: false }
        }
        console.log('check invitee failed, treated as invitable : %o', error)
        return { email, invitable: true }
      }
    }),
  )
}

/**
 * Send calendar invite to another user
 *
 * @param calendarUrl - Calendar URL
 * @param calendarName - Calendar name
 * @param toEmail - Recipient email
 * @param isReadOnly - Whether to grant read-only access
 * @param completeCallback - Callback with success status
 */
export const sendInviteCalendar = (
  calendarUrl: string,
  calendarName: string,
  toEmail: string,
  isReadOnly: boolean,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  const shareeEmail = toShareeEmail(toEmail)

  const xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<D:share-resource xmlns:D="DAV:">' +
    '<D:sharee>' +
    `<D:href>mailto:${shareeEmail}</D:href>` +
    '<D:prop>' +
    `<D:displayname>${calendarName}</D:displayname>` +
    '</D:prop>' +
    `<D:comment>${calendarName}</D:comment>` +
    `<D:share-access><D:${isReadOnly ? 'read' : 'read-write'}/></D:share-access>` +
    '</D:sharee>' +
    '</D:share-resource>'

  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'POST',
        body: xml,
      },
    })
    .then((response) => {
      console.log('invite calendar success %o', response)
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('invite calendar failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Remove calendar invite for a user
 *
 * @param calendarUrl - Calendar URL
 * @param toEmail - User email to remove access from
 * @param completeCallback - Callback with success status
 */
export const deleteInviteCalendar = (
  calendarUrl: string,
  toEmail: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  const shareeEmail = toShareeEmail(toEmail)

  const xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<D:share-resource xmlns:D="DAV:"><D:sharee>' +
    `<D:href>mailto:${shareeEmail}</D:href>` +
    '<D:share-access><D:no-access /></D:share-access>' +
    '</D:sharee></D:share-resource>'

  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'POST',
        body: xml,
      },
    })
    .then((response) => {
      console.log('delete invite calendar success %o', response)
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('delete invite calendar failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Set the calendar view type in settings
 *
 * @param viewType - View type to set
 */
export const setViewType = (viewType: CalendarViewType): void => {
  const localSettings = settings.getValue('localSettings')
  settings.setValue('localSettings', {
    ...localSettings,
    calendar: {
      ...localSettings.calendar,
      calendarViewType: viewType,
    },
  })
}
